import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Yup from 'yup';
import { Chip, TextField } from '@mui/material';
import Dropzone from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { MdClose } from 'react-icons/md';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { LinkTypes } from 'helpers';
import { makeFormStyles } from '../styles';
import { FormActions } from 'helpers/form-action';
import SetDirtyForm from 'forms/SetDirtyForm';
import { FormTextField } from 'components/TextField';
import { closePanel } from 'features/common/formSlice';
import { checkUnique } from 'helpers/checkUnique';
import { panelStyles } from 'GeminiViewerComponent/components/styles';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import {
    setProgress,
    activateLoading,
    deactivateLoading,
} from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { createClient, fetchClientById, editClient, selectClients } from 'features/clients/clientsSlice';
import { BiShow } from 'react-icons/bi';
import { CheckboxWithLabel } from 'formik-mui';
import AppButton from 'components/AppButton';
import { PasswordUtils } from 'GeminiViewerComponent/helpers/passwordUtils';
import { accountsSlice } from 'app/store';
const { addAvailableClient } = accountsSlice;

function ImageDropZoneField(props) {
    return (
        <Field className={props.input} name={props.name} type="file">
            {({ field: { value }, form: { setFieldValue } }) => (
                <div className={props.panelClasses.dropzoneContainer}>
                    <Dropzone
                        accept={LinkTypes.Image.contentTypes.map((at) => at.extension)}
                        maxFiles={1}
                        maxSize={LinkTypes.Image.fileSizeLimit}
                        multiple={false}
                        onDropAccepted={(files) => {
                            props.onDrop({
                                file: files[0],
                                setFieldValue: setFieldValue,
                                clientIconAvailable: props.values[`${props.name}_available`],
                            });
                        }}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <div
                                {...getRootProps()}
                                className={props.panelClasses.dropzoneArea}
                                style={{
                                    backgroundImage: `url(${props.values?.image_url})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'contain',
                                    backgroundPosition: 'center center',
                                }}
                            >
                                <input {...getInputProps()} />
                                {!props.values.image_url && (
                                    <div className={props.panelClasses.dropzoneText}>{props.label}</div>
                                )}
                            </div>
                        )}
                    </Dropzone>
                    <div className={props.panelClasses.previewContainer}>
                        {(value && Object.keys(value).length > 0) || props.values?.image_url ? (
                            <Chip
                                className={props.panelClasses.previewUploadFileName}
                                color="secondary"
                                size="small"
                                deleteIcon={<MdClose className="react-icon" />}
                                onDelete={() => {
                                    setFieldValue(props.name, null);
                                    setFieldValue(`${props.name}_url`, '');
                                }}
                                label={value?.name}
                            />
                        ) : null}
                    </div>
                </div>
            )}
        </Field>
    );
}

const ClientForm = ({ formAction, clientId }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const clients = useSelector(selectClients);
    // eslint-disable-next-line no-unused-vars
    const [loaded, setLoaded] = useState(formAction.id === FormActions.Create.id ? true : false);
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    const panelClasses = panelStyles(theme);
    // eslint-disable-next-line no-unused-vars
    const INIT_VALUES = {
        display_name: '',
        nav_image_url: '',
        item_image_url: '',
        nav_image_remove: false,
        item_image_remove: false,
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_password: '',
        showPassword: false,
        copy_themes: true,
    };
    const [initValues, setInitValues] = useState({ ...INIT_VALUES });
    const clientNameRef = useRef(null);

    useEffect(() => {
        if (formAction.id === FormActions.Edit.id) {
            const fetchData = async () => {
                var objectData = await dispatch(fetchClientById(clientId));
                if (!objectData.payload) {
                    const { message, variant } = snackbarHandler(
                        objectData.meta.requestStatus,
                        formAction.label,
                        'Could not load client'
                    );
                    enqueueSnackbar(message, {
                        action: (key) => <SnackbarDismiss key={key} />,
                        variant: variant,
                    });
                }
                var fetchedObject = objectData.payload;
                setInitValues({
                    display_name: fetchedObject.display_name,
                    nav_image_url: fetchedObject.nav_icon,
                    item_image_url: fetchedObject.item_icons,
                    nav_image_remove: false,
                    item_image_remove: false,
                    admin_first_name: '',
                    admin_last_name: '',
                    admin_email: '',
                    admin_password: '',
                    copy_themes: true,
                });
                setLoaded(true);
            };
            fetchData();
        } else {
            setInitValues({ ...INIT_VALUES });
        }
    }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async (values, setSubmitting, resetForm) => {
        dispatch(activateLoading({ showProgress: true }));
        var resultAction = null;

        switch (formAction.id) {
            case FormActions.Edit.id:
                resultAction = await dispatch(editClient({ client_id: clientId, ...values, dispatch }));
                if (!resultAction.error) {
                    dispatch(closePanel({ formKey: 'clientForm' }));
                }
                break;
            case FormActions.Create.id:
                dispatch(
                    setProgress({
                        progress: 0,
                        label: 'Step 1 of 2: Uploading',
                        noAnimation: true,
                    })
                );
                resultAction = await dispatch(createClient({ ...values, dispatch }));
                if (!resultAction.error) {
                    resetForm();
                    dispatch(
                        addAvailableClient({
                            client_id: resultAction.payload.client_id,
                            client_name: resultAction.payload.display_name,
                        })
                    );
                    if (clientNameRef.current) {
                        clientNameRef.current.focus();
                    }
                }
                break;
            default:
                break;
        }

        dispatch(deactivateLoading());
        const { message, variant } = snackbarHandler(resultAction.meta.requestStatus, formAction.label);
        enqueueSnackbar(message, {
            action: (key) => <SnackbarDismiss key={key} />,
            variant: variant,
        });
        setSubmitting(false);
    };

    const handleShow = (setValue, value) => {
        setValue('showPassword', !value);
    };

    const onNavDrop = useCallback(({ file, setFieldValue, clientIconAvailable }) => {
        setFieldValue('nav_image', file);
        const reader = new FileReader();

        reader.onabort = () => console.log('file reading was aborted');
        reader.onerror = () => console.log('file reading has failed');
        reader.onload = (e) => {
            setFieldValue('nav_image_url', e.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const onItemDrop = useCallback(({ file, setFieldValue, clientIconAvailable }) => {
        setFieldValue('item_image', file);
        const reader = new FileReader();

        reader.onabort = () => console.log('file reading was aborted');
        reader.onerror = () => console.log('file reading has failed');
        reader.onload = (e) => {
            setFieldValue('item_image_url', e.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const validateFields = useCallback(
        (values) => {
            let errors = {};
            if (formAction.id === FormActions.Create.id) {
                if (checkUnique(clients, 'display_name', 'client_id', values?.display_name)) {
                    errors = {
                        ...errors,
                        display_name: 'Must be unique client name.',
                    };
                }
            } else {
                if (checkUnique(clients, 'display_name', 'client_id', values?.display_name, clientId)) {
                    errors = {
                        ...errors,
                        display_name: 'Must be unique client name.',
                    };
                }
            }
            return errors;
        },
        [formAction.id, clientId, clients]
    );

    var passwordValidation = Yup.string()
        .min(8, 'Passwords must be at least 8 characters long and contain a capitalized letter and a number')
        .matches(/[0-9]+/, 'Passwords must be at least 8 characters long and contain a capitalized letter and a number')
        .matches(
            /[A-Z]+/,
            'Passwords must be at least 8 characters long and contain a capitalized letter and a number'
        );

    if (formAction.id === FormActions.Create.id) {
        passwordValidation.required('Required');
    }

    return !loaded ? (
        <LoadingLogo styleClassName={classes.loadingSvg} />
    ) : (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...initValues }}
            validate={validateFields}
            validationSchema={Yup.object({
                display_name: Yup.string().max(150, 'Must be 150 characters or less').required('Required'),
                admin_email: Yup.string().email(),
                admin_password: passwordValidation,
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                handleSubmit(values, setSubmitting, resetForm);
            }}
        >
            {({ isSubmitting, values, setFieldValue, errors }) => (
                <Form className={classes.form}>
                    <div className={classes.formHeader}>Client Details</div>
                    <FormTextField
                        inputRef={clientNameRef}
                        autoFocus
                        label="Client Name"
                        name="display_name"
                        placeholder="Client Name"
                        error={!!errors?.display_name}
                    />
                    {formAction.id === FormActions.Create.id && (
                        <>
                            {values['display_name'].length > 1 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%',
                                        padding: '10px',
                                    }}
                                >
                                    <AppButton
                                        variant="contained"
                                        className="primaryButton"
                                        handleButtonClick={(e) => {
                                            setFieldValue('admin_first_name', 'Super');
                                            setFieldValue('admin_last_name', 'Admin');
                                            setFieldValue(
                                                'admin_email',
                                                `admin.${values['display_name']
                                                    .replace(/\s+/g, '')
                                                    .toLowerCase()}@bgworkforce.com`
                                            );
                                            setFieldValue('admin_password', PasswordUtils.generatePassword());
                                            setFieldValue('showPassword', true);
                                        }}
                                    >
                                        Autofill Admin Info
                                    </AppButton>
                                </div>
                            )}
                            <FormTextField
                                label="Super Admin First Name"
                                name="admin_first_name"
                                placeholder="Super Admin First Name"
                            />
                            <FormTextField
                                label="Super Admin Last Name"
                                name="admin_last_name"
                                placeholder="Super Admin Last Name"
                            />
                            <FormTextField
                                label="Super Admin Email"
                                name="admin_email"
                                placeholder="Super Admin Email"
                            />

                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <TextField
                                    margin="dense"
                                    variant="outlined"
                                    className={classes.materialInput}
                                    value={values?.admin_password || ''}
                                    label="Admin Password"
                                    name="admin_password"
                                    placeholder="Admin Password"
                                    type={values.showPassword ? 'text' : 'password'}
                                    style={{ width: '90%' }}
                                    onChange={(e) => {
                                        setFieldValue('admin_password', e.target.value);
                                    }}
                                    error={!!errors?.admin_password}
                                    helperText={errors?.admin_password}
                                />
                                <div
                                    style={{
                                        padding: '10px',
                                        color: '#34485E',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <BiShow size={30} onClick={() => handleShow(setFieldValue, values.showPassword)} />
                                </div>
                            </div>
                            <FormTextField
                                component={CheckboxWithLabel}
                                name="copy_themes"
                                Label={{
                                    label: 'Copy themes from current client?',
                                }}
                                type="checkbox"
                                checked={!!values?.copy_themes}
                                placeholder="Copy themes from current client?"
                            />
                            <ImageDropZoneField
                                name="nav_image"
                                label="Drop Navigation Hotspot Icon Here"
                                input={classes.input}
                                panelClasses={panelClasses}
                                onDrop={onNavDrop}
                                values={values}
                            ></ImageDropZoneField>
                            <ImageDropZoneField
                                name="item_image"
                                label="Drop Item Hotspot Icon Here"
                                input={classes.input}
                                panelClasses={panelClasses}
                                onDrop={onItemDrop}
                                values={values}
                            ></ImageDropZoneField>
                        </>
                    )}
                    <SetDirtyForm />
                    <button className={classes.submit} type="submit" disabled={isSubmitting}>
                        {formAction.buttonLabel}
                    </button>
                </Form>
            )}
        </Formik>
    );
};

export { ClientForm };
